import clsx from 'clsx';
import type { ReactNode } from 'react';
import type { Control, FieldValues } from 'react-hook-form';

import type { SDSFlexboxGap } from '@repo/sds/SDSFlexbox';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSText } from '@repo/sds/SDSText';
// Note: Adjust path if FormFieldConfig type is moved or FormField export changes
import type { FormFieldConfig } from '@repo/ui/fixtures/form/components/FormField/FormField';
import { FormField } from '@repo/ui/fixtures/form/components/FormField/FormField';

import './FormGroup.scss';

/** Configuration defining the structure and content of a form group. */
export type FormGroupConfig<TFieldValues extends FieldValues> = {
  /** Array of individual field configurations within this group. */
  fields: FormFieldConfig<TFieldValues>[];
  /** Title displayed above the group, used for identification. */
  groupTitle: string;
  /** Optional descriptive text displayed below the title. */
  groupSubtitle?: string;
  /** CSS width declaration for the group container. */
  width?: string;
  /** CSS height declaration for the group container. */
  height?: string;
  /** Optional CSS class name(s) applied to the group's root element. */
  groupClassName?: string;
  /** Content outside the form's own values, laid out in the same row after the fields. */
  extraContent?: ReactNode;
};

/** Props required by the FormGroup component. */
interface FormGroupProps<
  TFieldValues extends FieldValues,
  TContext, // Inherits validation context type from the parent Form.
  TOutputType extends FieldValues = TFieldValues, // Inherits Zod output type from the parent Form.
> {
  /** The configuration object detailing this specific group. */
  groupConfig: FormGroupConfig<TFieldValues>;
  /** The react-hook-form control object, containing form state and methods. */
  control: Control<TFieldValues, TContext, TOutputType>;
  /** Flag indicating if all fields within the group should be disabled. */
  isDisabled: boolean;
  /** CSS gap value used between fields within the group's flex container. */
  gap: SDSFlexboxGap;
}

/** Renders a styled group of form fields, typically with a title and subtitle. */
export function FormGroup<TFieldValues extends FieldValues, TContext, TOutputType extends FieldValues = TFieldValues>({
  control,
  isDisabled,
  gap,
  groupConfig: { fields, groupTitle, groupSubtitle, groupClassName, extraContent, width = '100%', height = 'auto' },
}: FormGroupProps<TFieldValues, TContext, TOutputType>) {
  // Dynamically calculates inline styles for width and flex behavior,
  // adjusting based on the specified width and gap to ensure proper spacing in flex layouts.
  const formGroupStyle = {
    flex: width ? '1 1 auto' : '1', // Allows shrinking/growing only if a specific width is set.
    minWidth: 0,
    width: width === '100%' ? '100%' : `calc(${width} - var(--gaps-${gap}) / 2)`,
    maxWidth: width === '100%' ? '100%' : `calc(${width} - var(--gaps-${gap}) / 2)`, // Prevents exceeding calculated width.
    height: height,
  };

  const formGroupClassName = clsx('FormGroup', groupClassName);

  return (
    <div className={formGroupClassName} style={formGroupStyle}>
      <SDSText text={groupTitle} type="h5" className="FormGroup__title" />
      {groupSubtitle && <SDSText text={groupSubtitle} type="body-medium" className="FormGroup__subtitle" />}
      {/* Arranges the fields within the group using a flex container. */}
      <SDSFlexbox gap={gap} wrap="wrap" className="FormGroup__fields-container">
        {fields.map(fieldConfig => (
          <FormField
            key={fieldConfig.componentProps.name} // Field names must be unique within the form for keys and RHF state.
            fieldConfig={fieldConfig}
            control={control} // Passes down the control object.
            isDisabled={isDisabled}
            gap={gap} // Pass gap down for potential nested width calculations.
          />
        ))}
        {extraContent}
      </SDSFlexbox>
    </div>
  );
}
